import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, Filter, Plus, Trash2, Users } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { PERMISSIONS } from '@/lib/permissions'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'
import { EmptyState, PermissionDenied, Skeleton } from '@/components/States'
import {
  AUDIENCE_PRESETS,
  CAMP_STR as S,
  SEGMENT_FIELDS,
  SEGMENT_OPS,
  createSegment,
  fetchAudienceReach,
  fetchSegments,
  type SegmentRow,
  type SegmentRuleDraft,
} from './campaignsApi'

const BackLink = () => (
  <Link
    to="/broadcasts"
    className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
  >
    <ChevronLeft size={13} />
    {S.back}
  </Link>
)

/**
 * A15.03 — Auditoriya tanlash. Kampaniya bazaviy auditoriyalari (all/active/staff)
 * + fail-soft qamrov (reach). Segment konstruktoriga havola.
 */
export function AudienceSelect() {
  const { can } = useAuth()
  const [reach, setReach] = useState<Record<string, number> | null>(null)
  const [selected, setSelected] = useState('all')

  useEffect(() => {
    const ac = new AbortController()
    fetchAudienceReach(ac.signal).then((r) => setReach(r))
    return () => ac.abort()
  }, [])

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />

  return (
    <>
      <PageHeader
        title={S.audienceTitle}
        screenId="A15.03"
        description={S.audienceNote}
        actions={
          <div className="flex items-center gap-1.5">
            <Link
              to="/broadcasts/segments"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
            >
              <Filter size={13} />
              {S.openSegments}
            </Link>
            <BackLink />
          </div>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="grid max-w-3xl gap-3 sm:grid-cols-3">
          {AUDIENCE_PRESETS.map((a) => {
            const n = reach?.[a.key]
            const on = selected === a.key
            return (
              <button
                key={a.key}
                onClick={() => setSelected(a.key)}
                className={`rounded-lg border p-4 text-left transition-colors ${
                  on ? 'border-accent bg-accent-subtle' : 'border-border-default bg-surface-2 hover:border-border-strong'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Users size={15} className={on ? 'text-accent' : 'text-tertiary'} />
                  <span className="text-sm font-medium text-primary">{a.label}</span>
                </div>
                <p className="mt-1 text-xs text-secondary">{a.desc}</p>
                <div className="mt-3 text-xs text-tertiary">{S.reach}</div>
                <div className="text-lg font-semibold text-primary">
                  {reach === null ? '…' : typeof n === 'number' ? n.toLocaleString() : S.reachUnknown}
                </div>
              </button>
            )
          })}
        </div>
      </div>
    </>
  )
}

/**
 * A15.04 — Segment konstruktori. AND-shartlar bilan auditoriya yig'adi; saqlangan
 * segmentlar kampaniyalarda qayta ishlatiladi. Ro'yxat fail-soft (DORMANT backend).
 */
export function SegmentBuilder() {
  const { can } = useAuth()
  const { toast } = useToast()
  const [rows, setRows] = useState<SegmentRow[] | null>(null)
  const [tick, setTick] = useState(0)
  const reload = useCallback(() => setTick((t) => t + 1), [])

  const [name, setName] = useState('')
  const [audience, setAudience] = useState('all')
  const [rules, setRules] = useState<SegmentRuleDraft[]>([{ field: 'lang', op: 'eq', value: '' }])
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    fetchSegments(ac.signal).then((r) => setRows(r))
    return () => ac.abort()
  }, [tick])

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />

  const setRule = (i: number, patch: Partial<SegmentRuleDraft>) =>
    setRules((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)))
  const addRule = () => setRules((rs) => [...rs, { field: 'lang', op: 'eq', value: '' }])
  const delRule = (i: number) => setRules((rs) => (rs.length > 1 ? rs.filter((_, j) => j !== i) : rs))

  const save = async () => {
    const clean = rules.filter((r) => r.value.trim())
    if (!name.trim() || clean.length === 0) return
    setBusy(true)
    try {
      await createSegment({ name: name.trim(), audience, rules: clean })
      toast(S.saved, { tone: 'success' })
      setName('')
      setRules([{ field: 'lang', op: 'eq', value: '' }])
      reload()
    } catch {
      toast(S.failed, { tone: 'error', description: S.dormantNote })
    } finally {
      setBusy(false)
    }
  }

  const fieldLabel = (k: string) => SEGMENT_FIELDS.find((f) => f.key === k)?.label ?? k
  const opLabel = (k: string) => SEGMENT_OPS.find((o) => o.key === k)?.label ?? k

  return (
    <>
      <PageHeader title={S.segmentsTitle} screenId="A15.04" description={S.segmentsNote} actions={<BackLink />} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="mx-auto max-w-3xl space-y-4">
          <div className="space-y-3 rounded-lg border border-border-default bg-surface-2 p-3">
            <div className="flex flex-wrap items-center gap-2">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={S.segName}
                className="min-w-48 flex-1 rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
              />
              <select
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
                title={S.baseAudience}
                className="rounded-md border border-border-default bg-surface-1 px-2 py-2 text-sm text-primary"
              >
                {AUDIENCE_PRESETS.map((a) => (
                  <option key={a.key} value={a.key}>
                    {a.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <div className="text-xs font-medium uppercase tracking-wide text-tertiary">{S.rules}</div>
              {rules.map((r, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2">
                  <select
                    value={r.field}
                    onChange={(e) => setRule(i, { field: e.target.value })}
                    className="rounded-md border border-border-default bg-surface-1 px-2 py-1.5 text-sm text-primary"
                  >
                    {SEGMENT_FIELDS.map((f) => (
                      <option key={f.key} value={f.key}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                  <select
                    value={r.op}
                    onChange={(e) => setRule(i, { op: e.target.value })}
                    className="rounded-md border border-border-default bg-surface-1 px-2 py-1.5 text-sm text-primary"
                  >
                    {SEGMENT_OPS.map((o) => (
                      <option key={o.key} value={o.key}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <input
                    value={r.value}
                    onChange={(e) => setRule(i, { value: e.target.value })}
                    placeholder={S.value}
                    className="min-w-32 flex-1 rounded-md border border-border-default bg-surface-1 px-2.5 py-1.5 text-sm text-primary outline-none focus:border-accent"
                  />
                  <button
                    type="button"
                    onClick={() => delRule(i)}
                    className="rounded-md p-1.5 text-tertiary hover:bg-surface-3 hover:text-danger"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addRule}
                className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-1 px-2.5 py-1 text-sm text-secondary hover:text-primary"
              >
                <Plus size={13} />
                {S.addRule}
              </button>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-tertiary">{rules.every((r) => !r.value.trim()) ? S.noRules : ''}</span>
              <button
                disabled={busy || !name.trim() || rules.every((r) => !r.value.trim())}
                onClick={save}
                className="inline-flex items-center gap-1.5 rounded-md border border-accent bg-accent-subtle px-3 py-1.5 text-sm text-accent hover:bg-accent hover:text-white disabled:opacity-50"
              >
                <Filter size={13} />
                {S.createSegment}
              </button>
            </div>
          </div>

          <div>
            <div className="mb-2 text-xs font-medium uppercase tracking-wide text-tertiary">{S.savedSegments}</div>
            {rows === null ? (
              <div className="space-y-1.5">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-14 w-full" />
                ))}
              </div>
            ) : rows.length === 0 ? (
              <EmptyState icon={<Filter size={26} strokeWidth={1.5} />} title={S.empty} description={S.dormantNote} />
            ) : (
              <div className="space-y-2">
                {rows.map((seg) => (
                  <div key={seg.id} className="rounded-lg border border-border-default bg-surface-2 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-medium text-primary">{seg.name}</span>
                      <span className="text-xs text-tertiary">
                        {S.members}: {seg.count === null ? '—' : seg.count.toLocaleString()}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-secondary">
                      {(() => {
                        try {
                          const parsed = JSON.parse(seg.rules) as SegmentRuleDraft[]
                          return parsed.map((r) => `${fieldLabel(r.field)} ${opLabel(r.op)} ${r.value}`).join('  ·  ')
                        } catch {
                          return seg.rules
                        }
                      })()}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
